import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { getApiBaseUrl } from '@/lib/capacitor';
import {
  ArrowLeft,
  Image,
  Plus,
  Pencil,
  Trash2,
  Radio,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/toaster';

interface Gallery {
  _id: string;
  name: string;
  mqttTopic: string;
  createdAt: string;
}

interface FormData {
  name: string;
  mqttTopic: string;
}

const initialFormData: FormData = { name: '', mqttTopic: '' };

export default function ManageGalleriesPage() {
  const { authenticatedFetch } = useAuth();
  const [galleries, setGalleries] = useState<Gallery[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Add/Edit dialog state
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingGallery, setEditingGallery] = useState<Gallery | null>(null);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [isSaving, setIsSaving] = useState(false);

  // Delete dialog state
  const [deleteTarget, setDeleteTarget] = useState<Gallery | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchGalleries();
  }, []);

  const fetchGalleries = async () => {
    try {
      const response = await authenticatedFetch(`${getApiBaseUrl()}/api/galleries`);
      const data = await response.json();
      if (data.success) {
        setGalleries(data.galleries);
      }
    } catch (error) {
      console.error('Failed to fetch galleries:', error);
      toast({ title: 'Error', description: 'Failed to load galleries', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const openAddDialog = () => {
    setEditingGallery(null);
    setFormData(initialFormData);
    setIsFormOpen(true);
  };

  const openEditDialog = (gallery: Gallery) => {
    setEditingGallery(gallery);
    setFormData({ name: gallery.name, mqttTopic: gallery.mqttTopic });
    setIsFormOpen(true);
  };

  const closeFormDialog = () => {
    setIsFormOpen(false);
    setEditingGallery(null);
    setFormData(initialFormData);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim() || !formData.mqttTopic.trim()) {
      toast({ title: 'Error', description: 'Name and MQTT topic are required', variant: 'destructive' });
      return;
    }

    setIsSaving(true);
    try {
      const url = editingGallery
        ? `${getApiBaseUrl()}/api/galleries/${editingGallery._id}`
        : `${getApiBaseUrl()}/api/galleries`;
      const method = editingGallery ? 'PUT' : 'POST';

      const response = await authenticatedFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to save gallery');
      }

      toast({
        title: editingGallery ? 'Gallery updated' : 'Gallery added',
        description: `${formData.name} has been ${editingGallery ? 'updated' : 'added'}`,
      });
      closeFormDialog();
      fetchGalleries();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to save gallery';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    setIsDeleting(true);
    try {
      const response = await authenticatedFetch(`${getApiBaseUrl()}/api/galleries/${deleteTarget._id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete gallery');
      }

      toast({ title: 'Gallery deleted', description: `${deleteTarget.name} has been removed` });
      setDeleteTarget(null);
      fetchGalleries();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete gallery', variant: 'destructive' });
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-10 glass border-b border-border">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center gap-4">
          <Link to="/admin">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <Image className="w-5 h-5 text-primary" />
          <h1 className="font-display font-semibold flex-1">Manage Galleries</h1>
          <Button onClick={openAddDialog} size="sm">
            <Plus className="w-4 h-4 mr-2" />
            Add Gallery
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto p-4 space-y-4">
        {galleries.length === 0 ? (
          <div className="text-center py-12">
            <Image className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground mb-4">No galleries configured yet</p>
            <Button onClick={openAddDialog}>
              <Plus className="w-4 h-4 mr-2" />
              Add First Gallery
            </Button>
          </div>
        ) : (
          <div className="rounded-xl border border-border bg-card overflow-hidden">
            <div className="divide-y divide-border">
              {galleries.map((gallery) => (
                <div
                  key={gallery._id}
                  className="flex items-center gap-4 p-4 hover:bg-muted/50 transition-colors"
                >
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Image className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{gallery.name}</p>
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <Radio className="w-3 h-3" />
                      <span className="truncate font-mono">{gallery.mqttTopic}</span>
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEditDialog(gallery)}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive hover:text-destructive"
                      onClick={() => setDeleteTarget(gallery)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Add/Edit Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingGallery ? 'Edit Gallery' : 'Add Gallery'}</DialogTitle>
            <DialogDescription>
              {editingGallery
                ? 'Update the gallery name and MQTT topic.'
                : 'Add a new gallery to send content to via MQTT.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="name" className="text-sm font-medium">
                Name
              </label>
              <Input
                id="name"
                placeholder="Living Room Gallery"
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                disabled={isSaving}
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="mqttTopic" className="text-sm font-medium">
                MQTT Topic
              </label>
              <Input
                id="mqttTopic"
                placeholder="wallpaper/living-room/url-import"
                value={formData.mqttTopic}
                onChange={(e) => setFormData((prev) => ({ ...prev, mqttTopic: e.target.value }))}
                disabled={isSaving}
                className="font-mono"
              />
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="outline" disabled={isSaving}>
                  Cancel
                </Button>
              </DialogClose>
              <Button type="submit" disabled={isSaving}>
                {isSaving ? 'Saving...' : editingGallery ? 'Update' : 'Add'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Gallery</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove the gallery "{deleteTarget?.name}"?
              This will not affect any content already sent to this gallery.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
